import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import isEmpty from 'lodash-es/isEmpty';
import { Header, ConsumerPortalConfig } from '../../models/consumer-portal-config';
import { ConfigurationService } from '../../services/configuration.service';
import { ComponentsToLoad } from '../../enums/components-to-load.enum';
import { GapiLicense } from '../../enums/gapi-license';
import { LoggerService } from '../../services/logger.service';
import { Customer } from '../../models/consumer';
import { CONSUMER_PORTAL_APP_LANG } from '../../languages';
import { SelectOptions } from '../../shared/components/base-select/base-select.component';
import { TranslateService } from '@ngx-translate/core';
import { HamburgerItem } from '../../models/hamburger-model';
import { Subscription } from 'rxjs';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { LocalStorageKeys } from '../../enums/local-storage-keys.enum';
import { DEFAULT_LOCALE } from '../../constants/default.const';
import { AppFacadeService } from '../../services/app-facade.service';
import { AppMediatorService } from '../../services/app-mediator.service';
@Component({
    standalone: false,
    selector: 'por-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderComponent implements OnInit {
    private readonly configService = inject(ConfigurationService);
    private readonly logger = inject(LoggerService);
    private readonly translateService = inject(TranslateService);
    private readonly breakpointObserver = inject(BreakpointObserver);
    readonly appFacadeService = inject(AppFacadeService);
    private readonly appMediatorService = inject(AppMediatorService);

    customerId!: string;

    constructor() {
        const languageOptions = inject(CONSUMER_PORTAL_APP_LANG);

        this.languageOptions = languageOptions;
    }

    languageOptions: SelectOptions[];
    inputs: ConsumerPortalConfig | undefined;
    logo = '';
    uiUrl: string | undefined;
    header!: Header | undefined;
    @Input() config = '';
    subscriptions: Subscription[] = [];
    @Input() contractId: string | undefined;
    @Input() width: string | undefined;
    @Input() height: string | undefined;
    @Output() readonly elementLoaded = new EventEmitter<boolean>();
    @Output() readonly appOutput: EventEmitter<{ [key: string]: object }> = new EventEmitter<{ [key: string]: object }>();
    title: string | undefined;
    visible = true;
    @Input() logoPosition: Position | string | undefined;
    @Input() isLoginButtonVisible = true;
    logoWidth!: string;
    @Output() readonly logout = new EventEmitter();
    @Output() readonly manageUsers = new EventEmitter();

    @Output() readonly selectedLocale = new EventEmitter<string>();
    @Input() customer!: Customer | null;
    hamburgerItems: HamburgerItem[] = [
        {
            label: 'Home',
            page: ComponentsToLoad.AccountSummary
        },
        {
            label: 'Contracts',
            page: ComponentsToLoad.Contracts
        },
        {
            label: 'Invoices',
            page: ComponentsToLoad.Invoices
        },
        {
            label: 'Items Out',
            page: ComponentsToLoad.ItemsOut
        },
        {
            label: 'Tracking',
            page: ComponentsToLoad.Tracking
        }
    ];

    selectedLanguageOption!: string;
    isMobile = false;

    ngOnInit(): void {
        this.subscriptions.push(
            this.breakpointObserver.observe(['(max-width: 992px)']).subscribe((result: BreakpointState) => {
                this.isMobile = result.matches;
            })
        );
        const storedLanguage = this.appMediatorService.localStorageService.getItem(LocalStorageKeys.ContentLang)?.toString();
        this.selectedLanguageOption = storedLanguage != null ? storedLanguage : DEFAULT_LOCALE;
        this.configService.setMatIconFontFamily();
        if (this.config && !isEmpty(this.config)) {
            this.inputs = JSON.parse(this.config);
            this.logger.logAssert(this.inputs?.customerId, 'customerId is not defined');
            this.customerId = this.inputs?.customerId || '0';
            this.header = this.inputs?.header as Header;
            this.title = this.header.headerTitle;
        }
        if (this.inputs?.header?.logoUrl && this.showHeaderSection()) {
            this.logo = this.inputs?.header?.logoUrl;
        }
        if (this.inputs?.header?.logoWidth) {
            this.logoWidth = this.inputs?.header?.logoWidth;
        }
        this.elementLoaded.emit(true);
        this.hamburgerItems = this.hamburgerItems.filter((item: HamburgerItem) => {
            if (!this.appFacadeService.versionToggleService.isAdvanceVersion() && item?.page !== ComponentsToLoad.Contracts && item?.page !== ComponentsToLoad.Tracking) {
                return false;
            }
            if (item?.page === ComponentsToLoad.Invoices && !this.appFacadeService.featureToggleService.isAvailable('invoices')) {
                return false;
            }
            if (
                item?.page === ComponentsToLoad.Tracking &&
                (!this.appFacadeService.featureToggleService.isAvailable('canSeeTrackingTab') ||
                    !this.appFacadeService.featureToggleService.isAvailable('trackingTab') ||
                    !this.appFacadeService.versionToggleService.hasLicense(GapiLicense.ConsumerPortalTracking))
            ) {
                return false;
            }
            return true;
        });
        this.uiUrl = this.inputs?.uiUrl;
    }
    setHomePageTab(): void {
        if (this.header?.logoRedirectUrl) {
            window.location.href = this.header.logoRedirectUrl;
            return;
        }
        if (!this.appFacadeService.versionToggleService.isQuickLinkVersion()) {
            this.appFacadeService.setActiveTab(ComponentsToLoad.AccountSummary, true);
            this.appFacadeService.updateLocation(ComponentsToLoad.AccountSummary);
        }
    }

    changePage(tab: ComponentsToLoad | null): void {
        if (tab) {
            this.appFacadeService.setActiveTab(tab, true);
        }
    }

    handleSelectedLocale(value: string): void {
        this.appMediatorService.localStorageService.setItem(LocalStorageKeys.ContentLang, value);
        const locale = value ? value : DEFAULT_LOCALE;
        if (locale !== this.translateService.getCurrentLang()) {
            this.translateService.use(locale);
        }
    }

    showHeaderSection(): boolean {
        return (
            !(!this.appFacadeService.featureToggleService.isAvailable('showHeaderSections') && !this.appFacadeService.versionToggleService.isQuickLinkVersion()) ||
            this.appFacadeService.featureToggleService.isAvailable('showHeaderSections')
        );
    }

    openConsumerPortal(): void {
        if (this.appFacadeService.versionToggleService.isQuickLinkPdfExternalPortal() && this.header?.exterConsumerPortalUrl) {
            window.open(this.header?.exterConsumerPortalUrl, '_blank');
        } else {
            this.logout.emit();
        }
    }

    shouldShowNavigation(): boolean {
        return (
            (this.appFacadeService.versionToggleService.isAdvanceVersion() ||
                (this.appFacadeService.featureToggleService.isAvailable('canSeeTrackingTab') &&
                    this.appFacadeService.featureToggleService.isAvailable('trackingTab') &&
                    this.appFacadeService.versionToggleService.hasLicense(GapiLicense.ConsumerPortalTracking))) &&
            this.appFacadeService.versionToggleService.isNotQLOrConsumerQL()
        );
    }

    showLoginMoreInfoButton(): boolean {
        return this.appFacadeService.featureToggleService.isAvailable('canHaveQuickLinkLoginButton') && this.appFacadeService.versionToggleService.isQLButNotConsumerQL();
    }

    onClose(): void {
        this.visible = !this.visible;
    }

    openModal(): void {
        this.visible = true;
    }

    viewContacts() {
        this.manageUsers.emit();
    }

    isLogoDisplayed(): boolean {
        // Don't show logo if StoreName matches organizationId (show depot name instead)
        // StoreName is mapped to header.title in configuration.service.ts
        const storeName = this.header?.title;
        const organizationId = this.inputs?.organizationId;

        if (storeName && organizationId && storeName === organizationId) {
            return false; // Show depot name instead
        }

        // Show logo if it exists
        return this.logo !== undefined && this.logo !== null && this.logo !== '';
    }
}

export enum Position {
    Left = 'left',
    Right = 'right',
    Center = 'center'
}
